import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Cell
} from 'recharts';
import { TimeFilter } from '../../types';

interface DataPoint {
  period: string;
  /** Höhe des ganzen Balkens — bei gestapelten Balken die Summe der Anteile. */
  value: number;
  /*
   * Dazu bei gestapelten Balken je Anteil ein Feld mit dem Schlüssel aus
   * `segments`, Wert in kWh oder `null`, wenn der Anteil nicht bekannt ist.
   */
}

/**
 * Ein Anteil eines gestapelten Balkens, z. B. beim Strom der Netzbezug und der
 * Teil aus der PV-Anlage.
 */
export interface ChartSegment {
  key: string;
  label: string;
  color: string;
}

interface ExpandableMetricCardProps {
  title: string;
  value: number | null;
  unit: string;
  icon: React.ReactNode;
  color: 'indigo' | 'amber' | 'orange' | 'cyan';
  data: DataPoint[];
  filter: TimeFilter;
  loading?: boolean;
  currentReading?: number | null;
  /**
   * Was ein Balken misst — steht im Tooltip vor dem Wert. Bei einem
   * Stromzähler ist das der Bezug, bei einer PV-Anlage die Erzeugung.
   */
  seriesLabel: string;
  /**
   * Teilgrößen unter der grossen Zahl, z. B. bei PV die Aufteilung der
   * Erzeugung in Einspeisung und Eigenverbrauch.
   */
  breakdown?: Array<{ label: string; value: number | null }>;
  /** Kurze Einordnung unter der Aufschlüsselung, z. B. warum dort „—" steht. */
  breakdownNote?: string;
  /**
   * Teilt jeden Balken in gestapelte Anteile auf. Ohne Angabe ist der Balken
   * einfarbig und zeigt `value`.
   */
  segments?: ChartSegment[];
  /** Aktion oben im Kartenkopf, z. B. „Foto anfordern". */
  headerAction?: React.ReactNode;
  /** Zusatzinhalt im aufgeklappten Bereich, z. B. die Zählerfotos. */
  expandedExtra?: React.ReactNode;
}

const colorStyles = {
  indigo: 'from-[#216dab] to-[#3aa6e9] shadow-[#216dab]/30 text-white',
  amber: 'from-amber-500 to-amber-600 shadow-amber-500/30 text-white',
  orange: 'from-orange-500 to-orange-600 shadow-orange-500/30 text-white',
  cyan: 'from-cyan-500 to-cyan-600 shadow-cyan-500/30 text-white',
};

const formatXLabel = (period: string, filter: TimeFilter): string => {
  const date = new Date(period);
  switch (filter) {
    case 'TODAY':
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    case 'THIS_WEEK':
      return date.toLocaleDateString('de-DE', { weekday: 'short' });
    case 'THIS_MONTH':
    case 'LAST_MONTH':
      return date.toLocaleDateString('de-DE', { day: 'numeric' }) + '.';
    case 'THIS_YEAR':
    case 'LAST_YEAR':
      return date.toLocaleDateString('de-DE', { month: 'short' });
    default:
      return period;
  }
};

const formatFullDate = (period: string): string => {
  const date = new Date(period);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
};

const formatAmount = (value: number | null, unit: string): string =>
  value !== null ? `${value.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ${unit}` : '—';

const isDarkModeNow = () => document.documentElement.classList.contains('dark');

const roundValue = (v: number) => Math.round(v * 10) / 10;

const CustomTooltip = ({ active, payload, unit, seriesLabel, color, segments }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="glass p-3 shadow-xl border-none rounded-xl">
        <p className="text-sm font-medium text-slate-500 mb-1">
          {formatFullDate(data.period)}
        </p>
        {segments ? (
          <>
            {/* Oberster Anteil zuerst, wie im Balken. Unbekannte Anteile
                stehen mit „—" da statt mit einer erfundenen 0. */}
            {[...segments].reverse().map((s: ChartSegment) => (
              <p key={s.key} className="text-sm font-medium text-slate-600 dark:text-slate-300 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: s.color }} />
                {s.label}: {formatAmount(data[s.key], unit)}
              </p>
            ))}
            <p className="text-sm font-bold text-slate-700 dark:text-slate-200 mt-1 pt-1 border-t border-black/5 dark:border-white/10">
              {seriesLabel}: {formatAmount(data.value, unit)}
            </p>
          </>
        ) : (
          <p className="text-sm font-medium" style={{ color }}>
            {seriesLabel}: {payload[0].value} {unit}
          </p>
        )}
      </div>
    );
  }
  return null;
};

const ExpandableMetricCard: React.FC<ExpandableMetricCardProps> = ({
  title,
  value,
  unit,
  icon,
  color,
  data,
  filter,
  loading,
  currentReading,
  seriesLabel,
  breakdown,
  breakdownNote,
  segments,
  headerAction,
  expandedExtra,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formattedValue = value !== null
    ? value.toLocaleString('de-DE', { maximumFractionDigits: 2 }) + ' ' + unit
    : '—';

  const avg = data.length > 0 ? data.reduce((acc, d) => acc + d.value, 0) / data.length : 0;
  const isHigherThanAvg = value !== null && data.length > 0 && (value / data.length) > avg;

  const chartData = data.map((d) => {
    const point: Record<string, string | number | null> = {
      label: formatXLabel(d.period, filter),
      period: d.period,
      value: roundValue(d.value),
    };
    for (const s of segments ?? []) {
      const part = (d as unknown as Record<string, unknown>)[s.key];
      point[s.key] = typeof part === 'number' ? roundValue(part) : null;
    }
    return point;
  });

  // Die Summe steht über dem obersten Anteil — der liegt immer ganz oben im
  // Stapel, auch wenn er 0 ist.
  const valueLabel = (
    <LabelList
      dataKey="value"
      position="top"
      style={{
        fill: isDarkModeNow() ? '#cbd5e1' : '#1e293b',
        fontSize: '10px',
        fontWeight: '900',
        fontFamily: 'Outfit'
      }}
      offset={10}
    />
  );

const isDarkMode = isDarkModeNow();
  
  const getBarColor = () => {
    switch(color) {
      case 'indigo': return isDarkMode ? '#3aa6e9' : '#216dab';
      case 'amber': return '#f59e0b';
      case 'orange': return '#f97316';
      case 'cyan': return '#06b6d4';
      default: return '#216dab';
    }
  };

  return (
    <div className={`glass-card transition-all duration-700 ease-in-out border-black/5 dark:border-white/10 ${isExpanded ? 'lg:col-span-2 shadow-2xl scale-[1.01]' : 'hover:scale-[1.02]'}`}>
      <div 
        className="p-6 cursor-pointer select-none"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-3">
            <div className={`p-4 rounded-2xl bg-gradient-to-br transition-all duration-300 shrink-0 ${colorStyles[color]}`}>
              {icon}
            </div>
            {currentReading != null && (
              <div className="flex flex-col">
                <span className="text-sm font-medium text-slate-400 leading-none mb-1">Zählerstand</span>
                <span className="text-base font-bold text-slate-700 dark:text-slate-200 leading-none">
                  {currentReading.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 3 })} {unit}
                </span>
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-3">
             <div className="flex items-center gap-2">
                {headerAction}
                {loading ? (
                   <div className="h-5 w-16 bg-black/5 dark:bg-white/5 animate-pulse rounded-full" />
                ) : isHigherThanAvg ? (
                   <span className="flex items-center gap-1 text-sm font-medium text-rose-600 dark:text-rose-500 bg-rose-500/10 px-3 py-1 rounded-full border border-rose-500/20">
                      High
                   </span>
                ) : (
                   <span className="flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-500 bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20">
                      Normal
                   </span>
                )}
             </div>
             <div className={`p-1.5 rounded-full bg-black/5 dark:bg-white/5 transition-transform duration-500 ${isExpanded ? 'rotate-180' : ''}`}>
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                </svg>
             </div>
          </div>
        </div>

        <div className="space-y-1">
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors">{title}</p>
          {loading ? (
            <div className="h-10 w-32 bg-black/5 dark:bg-white/5 animate-pulse rounded-xl mt-2" />
          ) : (
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-black text-slate-800 dark:text-white tracking-tighter transition-colors">{formattedValue.split(' ')[0]}</span>
              <span className="text-sm font-medium text-slate-400 dark:text-slate-500 tracking-widest transition-colors">{formattedValue.split(' ')[1] || unit}</span>
            </div>
          )}
          {/* Teilgrößen der grossen Zahl — bei PV geht die Erzeugung in
              Einspeisung und Eigenverbrauch auf. */}
          {breakdown && breakdown.length > 0 && !loading && (
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 pt-2">
              {breakdown.map((item) => (
                <div key={item.label} className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-slate-400 dark:text-slate-500">{item.label}</span>
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                    {formatAmount(item.value, unit)}
                  </span>
                </div>
              ))}
            </div>
          )}
          {breakdownNote && !loading && (
            <p className="text-sm text-slate-400 dark:text-slate-500 pt-1">{breakdownNote}</p>
          )}
        </div>
      </div>

      {/* Die Höhe ist begrenzt, damit das Auf- und Zuklappen animiert. Mit
          Zusatzinhalt reichen 500px nicht — dann würde die Fotoliste
          abgeschnitten, ohne dass man es der Karte ansieht. */}
      <div className={`transition-all duration-700 ease-in-out overflow-hidden ${isExpanded ? `${expandedExtra ? 'max-h-[1100px]' : 'max-h-[500px]'} opacity-100 border-t border-black/5 dark:border-white/5` : 'max-h-0 opacity-0'}`}>
        <div className="p-8 bg-black/[0.01] dark:bg-white/[0.01]">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 30, right: 10, left: -25, bottom: 0 }}>
              <CartesianGrid strokeDasharray="4 4" stroke={isDarkMode ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.05)"} vertical={false} />
              <XAxis 
                dataKey="label" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: isDarkMode ? '#64748b' : '#94a3b8', fontSize: 10, fontWeight: 800 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={{ fill: isDarkMode ? '#64748b' : '#94a3b8', fontSize: 10, fontWeight: 800 }}
              />
              <Tooltip 
                cursor={{ fill: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}
                content={<CustomTooltip unit={unit} seriesLabel={seriesLabel} color={getBarColor()} segments={segments} />}
              />
              {segments ? (
                segments.map((s, i) => (
                  <Bar
                    key={s.key}
                    dataKey={(d: any) => d[s.key] ?? 0}
                    name={s.label}
                    stackId="segments"
                    fill={s.color}
                    fillOpacity={0.9}
                    radius={i === segments.length - 1 ? [8, 8, 0, 0] : [0, 0, 0, 0]}
                    maxBarSize={60}
                  >
                    {i === segments.length - 1 && valueLabel}
                  </Bar>
                ))
              ) : (
                <Bar
                  dataKey="value"
                  radius={[8, 8, 0, 0]}
                  maxBarSize={60}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getBarColor()} fillOpacity={0.85} />
                  ))}
                  {valueLabel}
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
          
          <div className="mt-8 flex items-center justify-between border-t border-black/5 dark:border-white/5 pt-6">
             {segments ? (
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                   {segments.map(s => (
                      <div key={s.key} className="flex items-center gap-2">
                         <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
                         <span className="text-sm font-medium text-slate-400">{s.label}</span>
                      </div>
                   ))}
                </div>
             ) : (
                <div className="flex items-center gap-2">
                   <div className={`w-2 h-2 rounded-full shadow-[0_0_10px_rgba(0,0,0,0.2)]`} style={{ backgroundColor: getBarColor() }} />
                   <span className="text-sm font-medium text-slate-400">Detaillierter Verlauf</span>
                </div>
             )}
             <span className="text-sm font-medium text-slate-400">Einheit: {unit}</span>
          </div>

          {expandedExtra}
        </div>
      </div>
    </div>
  );
};

export default ExpandableMetricCard;
